import React, {useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {useDebugMode} from "../hooks/useDebugMode";
import StoryRail from "./StoryRail";
import {RECOMMENDATION_CONTEXTS} from "../config/recommendationContexts";

const PersonalisedSports = ({
  contextId,
  fetchRecommendationsForContext,
  data = [],
  isLoading = false,
  viewInterceptor,
  devModeEnabled = false,
}) => {
  const navigate = useNavigate();
  const debugMode = useDebugMode();

  // Fetch recommendations once on mount
  useEffect(() => {
    if (contextId) {
      fetchRecommendationsForContext(contextId);
    }
  }, [contextId, fetchRecommendationsForContext]);

  const personalisedStories = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data
      .map(
        ({
          id,
          score,
          baseScore,
          name,
          description,
          metadata,
          recommendationSource,
          recommendationExplanation,
        }) => {
          if (!metadata) return null;
          return {
            uuid: id,
            originalTitle: metadata.originalTitle || name || "Untitled",
            aiSummary: metadata.summary || description || "",
            image: metadata.image || "",
            source: metadata.source || "",
            link: metadata.link || "",
            aiCategorization: metadata.aiCategorization || "",
            timeToRead: metadata.timeToRead || "",
            createdDate: metadata.createdDate || "",
            matchPercentage:
              typeof baseScore === "number" ? Math.round(baseScore * 100) : null,
            recommendationSource: recommendationSource || "",
            recommendationExplanation: recommendationExplanation || null,
          };
        }
      )
      .filter(Boolean);
  }, [data]);


  const handleStoryClick = (story) => {
    if (!story?.uuid) return;
    try {
      sessionStorage.setItem(`neuron:article:${story.uuid}`, JSON.stringify(story));
    } catch { /* ignore */ }
    navigate(`/article/${story.uuid}`);
  };

  const railItems = personalisedStories.map((story) => ({
    id: story.uuid,
    title: story.originalTitle,
    summary: story.aiSummary,
    image: story.image,
    category: story.aiCategorization,
    meta: [story.source, story.timeToRead].filter(Boolean).join(" \u00b7 "),
    matchPercentage: story.matchPercentage,
    explanation: story.recommendationExplanation,
    source: story.recommendationSource,
    story,
  }));

  return (
    <StoryRail
      kicker="General"
      title="Across the paper"
      standfirst="The same catalogue, ordered by a different context."
      debugNote={`ctx ${RECOMMENDATION_CONTEXTS.general} · diversity(topic max 2) · filter type!=comment`}
      debugMode={debugMode}
      items={railItems}
      isLoading={isLoading}
      onSelect={(item) => handleStoryClick(item.story)}
      getItemRef={(id) => viewInterceptor?.getItemRef(id)}
      emptyTitle="The general feed is quiet"
      emptyBody="This context has no ranked stories to show right now."
    />
  );
};

export default PersonalisedSports;
